import { Link } from 'react-router-dom';
import { statusMeta, type Match } from '@/lib/site';

interface MatchCardProps {
  match: Match;
  compact?: boolean;
}

const statusStyles: Record<string, string> = {
  live: 'bg-accent-500 text-background-50',
  upcoming: 'bg-secondary-100 text-secondary-900',
  finished: 'bg-background-200 text-foreground-600',
};

export default function MatchCard({ match, compact = false }: MatchCardProps) {
  const isLive = match.status === 'live';
  const isFinished = match.status === 'finished';

  return (
    <Link
      to={`/detail/${match.id}`}
      className="group flex flex-col rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors overflow-hidden cursor-pointer"
    >
      <div className="flex items-center justify-between gap-2 px-4 pt-3.5">
        <span className="inline-flex items-center gap-1.5 text-xs text-foreground-600">
          <i className="ri-trophy-line text-primary-600" />
          {match.leagueLabel}
          <span className="text-foreground-300">·</span>
          <span className="truncate max-w-[9rem]">{match.round.split('·')[1]?.trim() || match.round}</span>
        </span>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${statusStyles[match.status]}`}
        >
          {isLive && <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse-dot" />}
          {statusMeta[match.status].label}
        </span>
      </div>

      <div className="px-4 pt-3 pb-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-2 min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-md bg-primary-50 text-primary-700 font-heading text-[11px] flex items-center justify-center shrink-0">
                {match.home.shortName}
              </span>
              <span className="text-sm text-foreground-900 truncate group-hover:text-primary-700 transition-colors">
                {match.home.name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-md bg-secondary-50 text-secondary-700 font-heading text-[11px] flex items-center justify-center shrink-0">
                {match.away.shortName}
              </span>
              <span className="text-sm text-foreground-900 truncate group-hover:text-primary-700 transition-colors">
                {match.away.name}
              </span>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2 shrink-0">
            <span className={`font-heading text-xl leading-none ${isLive ? 'text-accent-600' : 'text-foreground-800'}`}>
              {isFinished || isLive ? match.home.score : '—'}
            </span>
            <span className={`font-heading text-xl leading-none ${isLive ? 'text-accent-600' : 'text-foreground-800'}`}>
              {isFinished || isLive ? match.away.score : '—'}
            </span>
          </div>
        </div>

        {compact ? (
          <div className="mt-3 flex items-center gap-1.5 pt-3 border-t border-background-200 text-xs text-foreground-600">
            <i className="ri-time-line" />
            {match.timeLabel}
          </div>
        ) : (
          <div className="mt-3 flex items-center justify-between gap-2 pt-3 border-t border-background-200">
            <span className="inline-flex items-center gap-1.5 text-xs text-foreground-600">
              <i className="ri-time-line" />
              {match.timeLabel}
            </span>
            <span className="inline-flex items-center gap-1 text-xs text-foreground-500 group-hover:text-accent-700 transition-colors whitespace-nowrap">
              {isLive ? '进入直播' : isFinished ? '查看回放' : '查看详情'}
              <i className="ri-arrow-right-line" />
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}